import { useViewerStore } from "../../../../../store/store";
import { Viewer } from "../Viewer-Factory/Viewer";
import { applyTreeGhostAppearance, applyTreeSelectionAppearance } from "./TreeGhostVisual";
import { GetRoundNum } from "./Common";
import {
    getTreeImpactForEligibility,
    hasCompletedGhostImpactReevaluation,
    isGhostOnlyGreenImpactTreesEnabled,
    isGreenImpactTree,
    TREE_IMPACT_GREEN_THRESHOLD,
} from "./EPCControl";
import { AlertType } from "../../../../../@types/enums";

type UnGhostedTreeDetail = {
    treeId: string;
    impactAtGhost: number;
};

/** MODULE_GRAPH may run more than once per manual ghost toggle (batched needsUpdate). */
export const GHOST_REEVAL_DEFERRAL_PASSES = 2;

let finalizeModuleGraphDepth = 0;

/** Finalize modal runs production calcs even when AI batch flags are set. */
export function beginFinalizeModuleGraph(): void {
    finalizeModuleGraphDepth += 1;
}

export function endFinalizeModuleGraph(): void {
    finalizeModuleGraphDepth = Math.max(0, finalizeModuleGraphDepth - 1);
}

function isFinalizeModuleGraphActive(): boolean {
    return finalizeModuleGraphDepth > 0;
}

export function isModuleGraphFinalizeActive(): boolean {
    return isFinalizeModuleGraphActive();
}

/** Whether MODULE_GRAPH should execute (skipped during AI batch unless finalizing). */
export function shouldRunModuleGraphCalc(): boolean {
    if (isFinalizeModuleGraphActive()) return true;
    return !isAiRunMode();
}

type TreeGhostUserData = {
    impactAtGhost?: number;
    activeTreeImpact?: number;
    hasReevaluatedSinceGhost?: boolean;
    ghostReevalDeferrals?: number;
    skipGhostReevalOnce?: boolean;
    /** User manually ghosted this tree; skip impact re-eval until they unghost it. */
    manualGhostFrozen?: boolean;
};

/** True while a manual ghost/unghost toggle's shade chain is still settling. */
let manualGhostToggleActive = false;

export function beginManualGhostToggle() {
    manualGhostToggleActive = true;
}

export function endManualGhostToggleIfIdle() {
    if (!useViewerStore.getState().refreshingShade) {
        manualGhostToggleActive = false;
    }
}

export function setManualGhostFrozen(tree: { userData?: TreeGhostUserData }, frozen: boolean) {
    tree.userData ??= {};
    if (frozen) {
        tree.userData.manualGhostFrozen = true;
    } else {
        delete tree.userData.manualGhostFrozen;
    }
}

/** Ghost a tree for an AI tree-cutting scenario; blocks auto-unghost after layout/shade settles. */
export function ghostTreeForTreeCutScenario(tree: {
    userData?: TreeGhostUserData;
    treeImpact?: number;
    delStatus?: boolean;
}) {
    const impactAtGhost = tree.treeImpact ?? 0;
    tree.userData ??= {};
    tree.userData.impactAtGhost = impactAtGhost;
    tree.userData.activeTreeImpact = impactAtGhost;
    tree.userData.hasReevaluatedSinceGhost = false;
    setManualGhostFrozen(tree, true);
    armGhostReevalDeferral(tree);
    applyTreeGhostAppearance(tree as any, true);
}

/** Clear scenario-ghost metadata when restoring trees to the live (unghosted) state. */
export function clearTreeCutScenarioGhostState(tree: { userData?: TreeGhostUserData }) {
    clearGhostReevalDeferral(tree);
    setManualGhostFrozen(tree, false);
    if (!tree.userData) return;
    delete tree.userData.impactAtGhost;
    delete tree.userData.activeTreeImpact;
    delete tree.userData.hasReevaluatedSinceGhost;
}

export function clearManualGhostFrozenOnOtherGhostedTrees(
    trees: { userData?: TreeGhostUserData; delStatus?: boolean }[],
    selectedTreeIds: Set<string>
) {
    trees.forEach((tree) => {
        const treeId = tree.userData?.treeId;
        if (tree.delStatus && treeId && !selectedTreeIds.has(treeId)) {
            delete tree.userData?.manualGhostFrozen;
        }
    });
}

function isManualGhostFrozen(tree: { userData?: TreeGhostUserData }): boolean {
    return tree.userData?.manualGhostFrozen === true;
}

export function armGhostReevalDeferral(tree: { userData?: TreeGhostUserData }, passes = GHOST_REEVAL_DEFERRAL_PASSES) {
    tree.userData ??= {};
    tree.userData.ghostReevalDeferrals = passes;
    delete tree.userData.skipGhostReevalOnce;
}

export function clearGhostReevalDeferral(tree: { userData?: TreeGhostUserData }) {
    if (!tree.userData) return;
    delete tree.userData.ghostReevalDeferrals;
    delete tree.userData.skipGhostReevalOnce;
}

function isManualGhostToggleSettling(tree: { delStatus?: boolean }): boolean {
    return manualGhostToggleActive && !!tree.delStatus;
}

function remainingGhostReevalDeferrals(tree: { userData?: TreeGhostUserData }): number {
    const deferrals = tree.userData?.ghostReevalDeferrals ?? 0;
    if (deferrals > 0) return deferrals;
    return tree.userData?.skipGhostReevalOnce ? 1 : 0;
}

/** Skip re-eval / auto-unghost while the ghost-toggle shade chain is still settling. */
export function shouldDeferGhostReeval(tree: { userData?: TreeGhostUserData; delStatus?: boolean }): boolean {
    if (isManualGhostFrozen(tree)) return true;
    if (remainingGhostReevalDeferrals(tree) > 0) return true;
    if (isManualGhostToggleSettling(tree)) return true;
    return useViewerStore.getState().refreshingShade === true && !!tree.delStatus;
}

/** Trees the user manually ghosted must not be re-evaluated for auto-unghost eligibility. */
export function shouldSkipGhostImpactReevaluation(tree: { userData?: TreeGhostUserData }): boolean {
    return isManualGhostFrozen(tree);
}

export function consumeGhostReevalDeferral(tree: { userData?: TreeGhostUserData; delStatus?: boolean }): boolean {
    if (isManualGhostFrozen(tree)) {
        tree.userData!.activeTreeImpact =
            tree.userData!.impactAtGhost ?? tree.userData!.activeTreeImpact ?? 0;
        return true;
    }

    const deferrals = tree.userData?.ghostReevalDeferrals ?? 0;
    if (deferrals > 0) {
        tree.userData!.ghostReevalDeferrals = deferrals - 1;
        tree.userData!.activeTreeImpact =
            tree.userData!.impactAtGhost ?? tree.userData!.activeTreeImpact ?? 0;
        return true;
    }

    if (tree.userData?.skipGhostReevalOnce) {
        delete tree.userData.skipGhostReevalOnce;
        tree.userData.activeTreeImpact =
            tree.userData.impactAtGhost ?? tree.userData.activeTreeImpact ?? 0;
        return true;
    }

    if (isManualGhostToggleSettling(tree) || (useViewerStore.getState().refreshingShade && tree.delStatus)) {
        tree.userData ??= {};
        tree.userData.activeTreeImpact =
            tree.userData.impactAtGhost ?? tree.userData.activeTreeImpact ?? 0;
        return true;
    }

    return false;
}

/** Deferred until the next SetModuleGraph after reCalcShade updates tree impacts. */
let pendingUnghostAlert: { details: UnGhostedTreeDetail[]; skipPasses: number } | null = null;

/** Nested apply-tree-scenario / open-3D flows intentionally ghost or restore trees. */
let treeScenarioLayoutSuppressDepth = 0;

/** Suppress auto-unghost while applying or opening a tree-cutting scenario layout. */
export function beginTreeScenarioLayoutSuppress(): void {
    treeScenarioLayoutSuppressDepth += 1;
}

export function endTreeScenarioLayoutSuppress(): void {
    treeScenarioLayoutSuppressDepth = Math.max(0, treeScenarioLayoutSuppressDepth - 1);
}

function isTreeScenarioLayoutSuppressActive(): boolean {
    if (treeScenarioLayoutSuppressDepth > 0) return true;
    const applyingScenarioIndex = (useViewerStore.getState() as { applyingScenarioIndex?: number | null })
        .applyingScenarioIndex;
    return applyingScenarioIndex != null;
}

/** True while Run AI / layout-variant batching is mutating ghost trees intentionally. */
export function isAiRunMode(): boolean {
    const state = useViewerStore.getState() as {
        aiLoading?: boolean;
        aiBatchLayoutMutating?: boolean;
        aiModuleFillingLoading?: boolean;
        treeReprocessInProgress?: boolean;
        reRunningTreeScenarios?: boolean;
    };
    return Boolean(
        state.aiLoading ||
            state.aiBatchLayoutMutating ||
            state.aiModuleFillingLoading ||
            state.treeReprocessInProgress ||
            state.reRunningTreeScenarios ||
            isTreeScenarioLayoutSuppressActive(),
    );
}

/** Drop a queued un-ghost alert (e.g. when Run AI starts). */
export function clearPendingUnghostAlert(): void {
    pendingUnghostAlert = null;
}

function getTreeImpactAfterRecalc(self: Viewer, treeId: string): number {
    const tree = self.treeGroup.children.find((t: any) => t.userData?.treeId === treeId) as any;
    if (!tree) return 0;

    const multiplierValue = tree.userData?.multiplier?.value;
    if (multiplierValue !== undefined && multiplierValue !== '') {
        const parsed = Number(multiplierValue);
        if (!Number.isNaN(parsed)) return parsed;
    }

    return tree.treeImpact ?? 0;
}

export function flushPendingUnghostAlert(self: Viewer) {
    if (!isGhostOnlyGreenImpactTreesEnabled() || isAiRunMode()) {
        pendingUnghostAlert = null;
        return;
    }
    if (!pendingUnghostAlert) return;

    if (pendingUnghostAlert.skipPasses > 0) {
        pendingUnghostAlert.skipPasses--;
        return;
    }

    const { details } = pendingUnghostAlert;
    pendingUnghostAlert = null;

    const treeList = details
        .map(
            (t) =>
                `Tree ${t.treeId}: multiplier at ghost ${GetRoundNum(t.impactAtGhost, 4)}, ` +
                `re-evaluated ${GetRoundNum(getTreeImpactAfterRecalc(self, t.treeId), 4)} (green requires > ${TREE_IMPACT_GREEN_THRESHOLD})`
        )
        .join("\n");

    self.viewerAlert({
        show: true,
        title: "Trees un-ghosted",
        message:
            "Ghosted trees were restored because they no longer qualify for ghosting after design changes:\n" +
            treeList,
        messageType: AlertType.warning,
        isCloseAfterDisplay: false,
    });
}

export function enforceGreenImpactTreeRules(self: Viewer) {
    if (!isGhostOnlyGreenImpactTreesEnabled() || isAiRunMode() || isFinalizeModuleGraphActive()) return;

    const eligibility: Record<string, boolean> = {};
    const unGhostedTreeIds: string[] = [];
    const unGhostedDetails: UnGhostedTreeDetail[] = [];

    self.treeGroup.children.forEach((tree: any) => {
        const treeId = tree.userData?.treeId;
        if (!tree.delStatus) {
            delete tree.userData.activeTreeImpact;
        }

        const impactForEligibility = getTreeImpactForEligibility(tree);
        const eligible =
            impactForEligibility !== undefined && isGreenImpactTree(impactForEligibility);
        if (treeId) {
            eligibility[treeId] = eligible;
            tree.userData.isGreenImpactEligible = eligible;
        }

        const shouldAutoUnghost =
            tree.delStatus &&
            !isManualGhostFrozen(tree) &&
            !shouldDeferGhostReeval(tree) &&
            hasCompletedGhostImpactReevaluation(tree) &&
            impactForEligibility !== undefined &&
            !isGreenImpactTree(impactForEligibility);

        if (shouldAutoUnghost) {
            const impactAtGhost = tree.userData.impactAtGhost ?? 0;
            applyTreeGhostAppearance(tree, false);
            if (tree.select) applyTreeSelectionAppearance(tree, true);
            if (treeId) {
                unGhostedTreeIds.push(treeId);
                unGhostedDetails.push({
                    treeId,
                    impactAtGhost,
                });
            }
            clearGhostReevalDeferral(tree);
            setManualGhostFrozen(tree, false);
            delete tree.userData.impactAtGhost;
            delete tree.userData.activeTreeImpact;
            delete tree.userData.hasReevaluatedSinceGhost;
        }
    });

    const { treeGreenImpactEligibilityVersion } = useViewerStore.getState();
    useViewerStore.setState({
        treeGreenImpactEligibility: eligibility,
        treeGreenImpactEligibilityVersion: treeGreenImpactEligibilityVersion + 1,
    });

    if (unGhostedTreeIds.length > 0) {
        pendingUnghostAlert = { details: unGhostedDetails, skipPasses: 1 };
        self.reCalcShade(unGhostedTreeIds, false);
    }
}